/* eslint-disable @typescript-eslint/no-explicit-any */
import {Catalog} from 'models/catalog';
import {FC, useContext} from 'react';
import {
  GET_CATALOGS,
  GET_MONETIZATION_CONFIGURATIONS,
} from 'graphql/absences/Querys';
import {authContext} from 'contextApi/context/auth';
import {formatDateUTC} from 'commons/dates';
import {useNavigate} from 'react-router-dom';
import {LeavesRoutes} from 'navigation/routes';
import {useLogger} from 'hooks/useLogger';
import {alertContext} from 'contextApi/context/error';
import {fileToBase64, isBrowserFile} from 'commons/files';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {END_AT_MORNING, START_AT_EVENING} from 'models/halfDay';

interface GraphqlError {
  message: string;
  code: string;
  extensions?: GraphqlErrorExtension;
}

interface GraphqlErrorExtension {
  code: string;
  message: string;
  serviceNames: string;
}

interface GraphqlErrors {
  graphQLErrors?: GraphqlError[];
  networkError?: Error | string;
  message?: string;
  name?: string;
}

interface CreateLeaveRequestEvidenceInput {
  evidenceType: string;
  imageValue: string;
  filename: string;
}

function isGraphqlError(obj: any): obj is GraphqlErrors {
  return obj && obj.graphQLErrors;
}

async function getEvidenceInputs(
  formData: any
): Promise<CreateLeaveRequestEvidenceInput[]> {
  const fileEntries = Object.entries(formData).filter(([, value]) =>
    isBrowserFile(value)
  );
  return Promise.all(
    fileEntries.map(async ([key, value]) => ({
      evidenceType: key,
      imageValue: await fileToBase64(value as File, true),
      filename: (value as File).name,
    }))
  );
}

async function buildVariablesMapper(
  formData: any,
  {isDraft, useSubLeaveType}: any,
  userId: string | undefined
) {
  const scheduleOptions: string[] = [];
  if (formData.leaveRangeDate.startCheck) {
    scheduleOptions.push(START_AT_EVENING);
  }
  if (formData.leaveRangeDate.endCheck) {
    scheduleOptions.push(END_AT_MORNING);
  }
  const evidences = await getEvidenceInputs(formData);
  return {
    leaveRequest: {
      userId,
      startDate: formatDateUTC(formData.leaveRangeDate.startDate),
      endDate: formatDateUTC(formData.leaveRangeDate.endDate),
      typeOfLeaveId: Number(
        useSubLeaveType ? formData.subLeaveTypeId : formData.leaveTypeId
      ),
      isDraft,
      scheduleOptions,
      ...(evidences.length > 0 ? {evidences} : {}),
    },
  };
}

async function submitLeaveRequest(
  mutationPromise: any,
  {log, dispatch, navigate}: {log: any; dispatch: any; navigate: any}
) {
  try {
    const response = await mutationPromise;
    const leaveRequest = response.data.createLeaveRequest;
    log.debug('Request created', leaveRequest.id);
    dispatch({
      type: 'show',
      payload: {
        level: 'success',
        message: 'Solicitud creada con éxito',
        duration: 'short',
        callback: () =>
          navigate(`../${LeavesRoutes.REQUEST_DETAIL}/${leaveRequest.id}`),
      },
    });
    return response;
  } catch (error: any) {
    if (isGraphqlError(error)) {
      const messages = error.graphQLErrors
        ?.map((err: any) => {
          log.debug('message', err);
          return err.extensions?.message || err.message;
        })
        ?.join('\n');
      log.error('Graphql error', messages);
      dispatch({
        type: 'show',
        payload: {message: messages || 'Algo salió mal', level: 'error'},
      });
    } else {
      log.error('Generic error', error);
    }
    throw error;
  }
}

function mergeLeaveTypes(
  leaveTypes: Catalog[],
  monetizationConfig: any
): Catalog[] {
  const monetizationLeaveTypes: Catalog[] = (monetizationConfig?.data || [])
    .map((config: any) => config.leaveType)
    .filter(Boolean)
    .filter(
      (lt: any, index: number, self: any[]) =>
        index === self.findIndex((t: any) => t.id === lt.id)
    );
  const existingIds = new Set(leaveTypes.map((lt: Catalog) => lt.id));
  return [
    ...leaveTypes,
    ...monetizationLeaveTypes.filter((lt: any) => !existingIds.has(lt.id)),
  ];
}

/**
 * View model specialized for the leave-request creation flow.
 * It serializes evidences into the create mutation so the backend can persist
 * the request and its files atomically.
 */
export const withCreateFormViewModel =
  () => (Component: FC<any>) => (props: any) => {
    const log = useLogger(withCreateFormViewModel.name);
    const {userDetails} = useContext(authContext);
    const navigate = useNavigate();
    const {dispatch} = useContext(alertContext);

    const variablesMapper = (formData: any, options: any) =>
      buildVariablesMapper(formData, options, userDetails?.data?.id);

    const submit = (mutationPromise: any) =>
      submitLeaveRequest(mutationPromise, {log, dispatch, navigate});

    return (
      <GqlQueryController
        QUERY={GET_CATALOGS}
        variables={{type: 'LEAVE_TYPE', country: userDetails?.data?.country}}
        initialState={[] as Catalog[]}
        rootField="catalogs"
      >
        {({data: leaveTypes}) => (
          <GqlQueryController
            QUERY={GET_MONETIZATION_CONFIGURATIONS}
            variables={{country: userDetails?.data?.country, status: 'ACTIVE'}}
            initialState={{data: []}}
            rootField="getMonetizationConfigurations"
          >
            {({data: monetizationConfig}) => (
              <Component
                leaveTypes={mergeLeaveTypes(leaveTypes, monetizationConfig)}
                variablesMapper={variablesMapper}
                onSubmit={submit}
                {...props}
              />
            )}
          </GqlQueryController>
        )}
      </GqlQueryController>
    );
  };

export default withCreateFormViewModel;
